import React, { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PasswordInput from "@/components/PasswordInput";
import { toast } from "sonner";
import { Loader2, ShieldCheck, ArrowLeft, KeyRound } from "lucide-react";

export default function AdminLogin() {
  const { user, refreshUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // If already signed in as admin, jump straight to the console.
  if (user && user.role === "admin") return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!email || !password) { toast.error("Enter your admin credentials."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email: email.trim(), password });
      // Admin accounts skip 2FA and the server returns the access token right away.
      // Regular users get a 2FA challenge — they should NOT log in via this URL.
      if (data.requires_2fa) {
        toast.error("This URL is for administrators only. Sign in at /login instead.");
        return;
      }
      const u = await refreshUser();
      if (!u || u.role !== "admin") {
        toast.error("This account is not an administrator.");
        return;
      }
      toast.success("Welcome, administrator.");
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A130E] px-4 py-10" data-testid="admin-login-page">
      <div className="absolute inset-0 -z-10 bg-gradient-to-br from-[#0A130E] via-[#0E1A14] to-[#15211A]" />
      <Link to="/" className="absolute top-6 left-6 inline-flex items-center gap-2 text-sm text-white/60 hover:text-white" data-testid="admin-login-home">
        <ArrowLeft className="w-4 h-4" /> Back to site
      </Link>

      <form
        onSubmit={submit}
        className="w-full max-w-md bg-[#15211A] border border-white/10 rounded-2xl p-8 shadow-2xl"
      >
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-xl bg-[#1B3526] flex items-center justify-center">
            <ShieldCheck className="w-6 h-6 text-[#9DCBAD]" strokeWidth={1.6} />
          </div>
          <div>
            <div className="text-eyebrow text-[#9DA39A]">FinVault</div>
            <h1 className="font-display text-2xl font-bold tracking-tight text-[#F0ECE0]">Administrator console</h1>
          </div>
        </div>
        <p className="text-sm text-[#9DA39A] mt-3 leading-relaxed">
          Restricted access. Audit-logged. Customer support? Use the main <Link to="/login" className="underline text-[#9DCBAD]">sign-in page</Link> instead.
        </p>

        <div className="mt-7 space-y-4">
          <div>
            <Label className="text-eyebrow text-[#9DA39A]">Admin email</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@finvault.capital"
              autoComplete="email"
              data-testid="admin-login-email"
              className="mt-1 bg-white/[0.04] border-white/10 text-[#F0ECE0] placeholder:text-white/30"
            />
          </div>
          <div>
            <Label className="text-eyebrow text-[#9DA39A]">Password</Label>
            <PasswordInput
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              placeholder="••••••••"
              data-testid="admin-login-password"
              className="mt-1 bg-white/[0.04] border-white/10 text-[#F0ECE0]"
            />
          </div>
        </div>

        <Button
          type="submit"
          disabled={loading}
          className="mt-7 w-full bg-[#9DCBAD] text-[#0A130E] hover:bg-[#7FBF96]"
          data-testid="admin-login-submit"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><KeyRound className="w-4 h-4 mr-2" /> Sign in to console</>}
        </Button>

        <p className="mt-6 text-[11px] text-[#9DA39A]/70 leading-relaxed text-center">
          All admin sessions are recorded. Unauthorized access is prohibited and may be prosecuted.
        </p>
      </form>
    </div>
  );
}
